/**
 * Archivos adjuntos manuales (pedido de la dueña 2026-10-19; decisión «Archivos adjuntos manuales en general»): una foto
 * o un PDF de hasta 3 MB en una compra (la factura del proveedor), un gasto, una retención (su comprobante), el reintegro
 * de una retención (el comprobante del pago), la ficha del cliente o un documento de venta. Viven en la base (migración
 * 059), no son archivos públicos; solo la dueña los borra, con motivo.
 */
import { z } from 'zod';
import type { Permiso } from '../permisos.ts';
import { uuid } from './comun.ts';

export const ENTIDADES_ADJUNTO = ['compra', 'gasto', 'retencion', 'reintegro', 'cliente', 'documento'] as const;
export type EntidadAdjunto = (typeof ENTIDADES_ADJUNTO)[number];

/** Solo fotos y PDF (la API revisa además que los primeros bytes digan lo mismo). */
export const TIPOS_ADJUNTO = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'] as const;
export type TipoAdjunto = (typeof TIPOS_ADJUNTO)[number];

/** Tope por archivo: 3 MB (también en la base, CHECK adjuntos_tamano). */
export const ADJUNTO_MAX_BYTES = 3 * 1024 * 1024;

export const TIPO_ADJUNTO_ETIQUETA: Record<TipoAdjunto, string> = { 'image/jpeg': 'JPG', 'image/png': 'PNG', 'image/webp': 'WEBP', 'application/pdf': 'PDF' };
export const EXTENSION_ADJUNTO: Record<TipoAdjunto, string> = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'application/pdf': 'pdf' };

/**
 * Quién ve y quién sube en cada cosa: ver = el permiso de lectura de lo que se adjunta; subir = quien lo opera. El
 * cajero que carga el comprobante de una retención sube su imagen; el comprobante del pago lo sube quien valida el
 * reintegro. Borrar es solo de la dueña.
 */
export const PERMISO_ADJUNTO: Record<EntidadAdjunto, { ver: Permiso; subir: Permiso }> = {
  compra: { ver: 'compras.registrar', subir: 'compras.registrar' },
  gasto: { ver: 'gastos.registrar', subir: 'gastos.registrar' },
  retencion: { ver: 'retenciones-clientes.cargar', subir: 'retenciones-clientes.cargar' },
  reintegro: { ver: 'retenciones-clientes.cargar', subir: 'retenciones.reintegro-validar' },
  cliente: { ver: 'clientes.crear', subir: 'clientes.crear' },
  documento: { ver: 'ventas.emitir', subir: 'ventas.emitir' },
};

/** Categoría que se propone al adjuntar en cada cosa (se puede dejar en blanco). */
export const CATEGORIA_ADJUNTO_SUGERIDA: Record<EntidadAdjunto, string> = {
  compra: 'Factura del proveedor',
  gasto: 'Soporte del gasto',
  retencion: 'Comprobante de retención',
  reintegro: 'Comprobante del pago',
  cliente: 'Documento del cliente',
  documento: 'Soporte del documento',
};

/** Tamaño para mostrar: «350 KB», «1,4 MB». */
export function tamanoLegible(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(Math.round((bytes / (1024 * 1024)) * 10) / 10).toFixed(1).replace('.', ',')} MB`;
}

/**
 * Lo que se le dice a quien adjunta un archivo de más de 3 MB (la pantalla y la API dicen lo mismo). Justo por encima del
 * tope se leería «pesa 3,0 MB; el máximo es 3 MB»: se dice «un poco más de 3 MB».
 */
export function mensajeArchivoMuyGrande(nombre: string, bytes: number): string {
  const pesa = tamanoLegible(bytes) === tamanoLegible(ADJUNTO_MAX_BYTES) ? 'un poco más de 3 MB' : tamanoLegible(bytes);
  return `El archivo «${nombre}» pesa ${pesa}; el máximo por archivo es 3 MB. Redúcelo (una foto más pequeña o un PDF comprimido) e inténtalo de nuevo`;
}

const entidad = z.enum(ENTIDADES_ADJUNTO, { error: 'No se adjuntan archivos ahí' });

/**
 * Subir: el archivo viaja en base64 dentro del JSON (como las fotos de las roturas). El tope del texto queda por encima
 * del tope del cuerpo de esta ruta (5 MB, app.ts): así el servidor siempre dice claro cuánto pesa el archivo y cuál es
 * el máximo (3 MB), en vez de un «datos inválidos».
 */
export const adjuntoSubirInput = z.object({
  entidad,
  entidad_id: uuid,
  categoria: z.string().trim().min(3, 'La categoría es muy corta').max(60, 'La categoría es muy larga').optional(),
  nombre: z.string().trim().min(1, 'Falta el nombre del archivo').max(200, 'El nombre del archivo es muy largo'),
  tipo: z.enum(TIPOS_ADJUNTO, { error: 'Solo se adjuntan fotos (JPG, PNG o WEBP) o PDF' }),
  contenido: z
    .string()
    .min(1, 'El archivo está vacío')
    .max(5_600_000, 'El archivo pasa del máximo de 3 MB por archivo')
    .regex(/^[A-Za-z0-9+/]+={0,2}$/, 'El archivo no llegó completo: vuelve a adjuntarlo'),
});
export type AdjuntoSubirInput = z.infer<typeof adjuntoSubirInput>;

export const adjuntosFiltros = z.object({ entidad, entidad_id: uuid });
export type AdjuntosFiltros = z.infer<typeof adjuntosFiltros>;

/** Cuántos archivos tiene cada una de varias cosas (p. ej. el comprobante del pago en el Historial de Retenciones). */
export const adjuntosConteoQuery = z.object({
  entidad,
  ids: z
    .string()
    .transform((s) => s.split(',').map((x) => x.trim()).filter((x) => x !== ''))
    .pipe(z.array(uuid).min(1, 'Indica al menos uno').max(100, 'Hasta 100 a la vez')),
});
export type AdjuntosConteoQuery = z.infer<typeof adjuntosConteoQuery>;

export const adjuntoBorrarInput = z.object({
  motivo: z.string().trim().min(5, 'Escribe el motivo (al menos 5 letras)').max(300, 'El motivo es muy largo'),
});
export type AdjuntoBorrarInput = z.infer<typeof adjuntoBorrarInput>;

export interface AdjuntoDTO {
  id: string;
  entidad: EntidadAdjunto;
  entidad_id: string;
  categoria: string | null;
  nombre: string;
  tipo: TipoAdjunto;
  tamano: number;
  sha256: string;
  subido_por_nombre: string;
  subido_en: string;
}
